'use client';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import Typography from '@alltrails/core/components/Typography';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import { minutesToFormattedTime } from '@alltrails/data-formatting/dateTimeFormatters';
import { getFormattedDistance } from '@alltrails/data-formatting/distanceFormatters';
import { ratingToFormattedRating } from '@alltrails/data-formatting/numberFormatters';
import Link from '@alltrails/denali/components/Link';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import DifficultyIcon from '@alltrails/difficulty/components/DifficultyIcon';
import useDifficultyText from '@alltrails/shared/hooks/useDifficultyText';
import type Trail from '@alltrails/types/Trail';
import { getUrlFromSlug } from '../../utils/cardLinks';
import MiddleDot from '../MiddleDot';
import styles from './styles/styles.module.scss';

type TrailMetadataProps = {
  trail: Trail | TrailDetails;
  useMetric: boolean;
  decimalPlaces?: number;
  openInNewTab?: boolean;
  baseUrl: string;
  displayRank?: number;
  variant?: 'small' | 'standard' | 'feed';
};

const TrailMetadata = ({
  trail,
  useMetric,
  decimalPlaces = 1,
  openInNewTab,
  baseUrl,
  displayRank,
  variant
}: TrailMetadataProps) => {
  const {
    area,
    defaultActivityStats: { difficulty },
    avgRating,
    name,
    trailGeoStats: { length: meters, durationMinutes, averageTimeToComplete }
  } = trail;
  const intl = useIntl();
  const locationName = area?.name;
  const locationSlug = area?.slug;
  const testIdPrefix = `TrailCard_${name}`;
  const localizedRating = ratingToFormattedRating(avgRating, intl);
  const difficultyText = useDifficultyText(difficulty);

  const locationElement: React.ReactNode = locationSlug ? (
    <Link
      className={styles.link}
      size="sm"
      variant="secondary"
      href={getUrlFromSlug(locationSlug, baseUrl)}
      noUnderline
      testId={`${testIdPrefix}_Location_Link`}
      target={openInNewTab ? '_blank' : undefined}
    >
      {locationName}
    </Link>
  ) : (
    locationName
  );

  const displayName = displayRank ? `#${displayRank} - ${name}` : name;

  const renderDuration = () => {
    if (!durationMinutes || durationMinutes === '0') return null;

    const renderEstTime = () => (
      <>
        <MiddleDot />
        {intl.formatMessage({ defaultMessage: 'Est.' })} {averageTimeToComplete && `${averageTimeToComplete.value} ${averageTimeToComplete.unit}`}
      </>
    );

    const renderFormattedTime = () => (
      <>
        <MiddleDot />
        {minutesToFormattedTime({ intl, totalMinutes: parseInt(durationMinutes, 10), isEstimate: true })}
      </>
    );

    return averageTimeToComplete ? renderEstTime() : renderFormattedTime();
  };

  const ratingAndDifficulty = (
    <>
      <span className={styles.star}>
        {/* Purposefully using an invalid size so we can override the inline height and width in CSS */}
        <StarFilled size={'snowflake' as any} color="--color-text-secondary" />
      </span>
      <span data-testid={`${testIdPrefix}_Rating`}>{localizedRating}</span>
      {difficultyText !== undefined && (
        <>
          <MiddleDot />
          {difficulty && (
            <span className={styles.difficultyIcon}>
              <DifficultyIcon difficulty={difficulty} size="xs" />
            </span>
          )}
          {difficultyText}
        </>
      )}
    </>
  );

  const lengthAndDuration = (
    <>
      {getFormattedDistance(intl, meters || 0, useMetric, { maximumFractionDigits: decimalPlaces })}
      {renderDuration()}
    </>
  );

  if (variant === 'small') {
    return (
      <div>
        <Typography variant="text200bold" className={styles.truncating} component="div">
          {displayName}
        </Typography>
        <Typography variant="text100" className={classNames(styles.location, styles.truncating)} component="div">
          {locationElement}
        </Typography>
        <div className={classNames(styles.metadata, styles.truncating)}>{ratingAndDifficulty}</div>
        <div className={classNames(styles.metadata, styles.truncating)}>{lengthAndDuration}</div>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="text200bold" className={styles.truncating} component="div">
        {displayName}
      </Typography>
      <Typography variant="text100" className={classNames(styles.location, styles.truncating)} component="div">
        {locationElement}
      </Typography>
      <div className={classNames(styles.metadata, styles.truncating)}>
        {ratingAndDifficulty}
        <MiddleDot />
        {getFormattedDistance(intl, meters || 0, useMetric, { maximumFractionDigits: decimalPlaces })}
        {renderDuration()}
      </div>
    </div>
  );
};

export default TrailMetadata;
